import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MapPin, Briefcase, Users, Shield } from "lucide-react";

interface CandidateBoardCardCandidate {
  id: number;
  first_name?: string;
  last_name?: string;
  status: string;
  main_skill?: string;
  seniority?: string;
  location?: string;
  gdpr_consent_status?: string;
  active_application_count?: number;
  owner_name?: string;
  responsible_recruiter_name?: string;
  kam_names?: string;
  open_for_more_projects?: boolean;
}

interface CandidateBoardCardProps {
  candidate: CandidateBoardCardCandidate;
  onNavigate: (candidateId: number) => void;
  isDragOverlay?: boolean;
}

export const CandidateBoardCard: React.FC<CandidateBoardCardProps> = ({
  candidate,
  onNavigate,
  isDragOverlay = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(candidate.id), disabled: isDragOverlay });

  const style = isDragOverlay
    ? undefined
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };

  const name =
    [candidate.first_name, candidate.last_name].filter(Boolean).join(" ") ||
    "Unknown";
  const appCount = candidate.active_application_count ?? 0;
  const hasGdpr = candidate.gdpr_consent_status === "given";

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={style}
      className={`status-kanban-card ${isDragging ? "status-kanban-card--dragging" : ""} ${isDragOverlay ? "status-kanban-card--overlay" : ""}`}
      data-testid={`board-card-candidate-${candidate.id}`}
      {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
    >
      <button
        type="button"
        className="status-kanban-card__title"
        onClick={(e) => {
          e.stopPropagation();
          onNavigate(candidate.id);
        }}
        style={{ cursor: "pointer" }}
      >
        {name}
        {hasGdpr && (
          <Shield
            size={11}
            style={{ marginLeft: 4, color: "#10b981", verticalAlign: "middle" }}
          />
        )}
      </button>

      <div className="status-kanban-card__meta">
        {candidate.main_skill && (
          <span
            className="status-kanban-card__meta-item"
            style={{ fontWeight: 600, color: "#4b5563" }}
          >
            {candidate.main_skill}
          </span>
        )}
        {candidate.seniority && (
          <span className="status-kanban-card__meta-item">
            <Briefcase size={11} />
            {candidate.seniority}
          </span>
        )}
      </div>

      <div className="status-kanban-card__meta">
        {candidate.location && (
          <span className="status-kanban-card__meta-item">
            <MapPin size={11} />
            {candidate.location}
          </span>
        )}
        {appCount > 0 && (
          <span className="status-kanban-card__meta-item">
            <Users size={11} />
            {appCount} app{appCount === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {candidate.status === "hired" && candidate.open_for_more_projects && (
        <div style={{ marginTop: 4 }}>
          <span
            className="status-kanban-card__badge"
            style={{ background: "#d1fae5", color: "#065f46" }}
          >
            Open for more projects
          </span>
        </div>
      )}

      {(candidate.owner_name ||
        candidate.responsible_recruiter_name ||
        candidate.kam_names) && (
        <div className="status-kanban-card__footer">
          {candidate.owner_name && <span>{candidate.owner_name}</span>}
          {candidate.responsible_recruiter_name && (
            <span className="status-kanban-card__personnel-item">
              Recruiter: {candidate.responsible_recruiter_name}
            </span>
          )}
          {candidate.kam_names && (
            <span className="status-kanban-card__personnel-item">
              KAM: {candidate.kam_names}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
